import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  where,
  onSnapshot,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';

const useTagStore = create(
  subscribeWithSelector((set, get) => ({
    // State
    tags: [],
    loading: false,
    error: null,
    unsubscribeTags: null,

    // Actions
    subscribeToTags: (familyId) => {
      if (!familyId) return;

      // Unsubscribe from previous subscription
      const { unsubscribeTags } = get();
      if (unsubscribeTags) {
        unsubscribeTags();
      }

      const tagsRef = collection(db, `families/${familyId}/tags`);
      const q = query(tagsRef, orderBy('name', 'asc'));

      const unsubscribe = onSnapshot(q, 
        (snapshot) => {
          const tags = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          set({ 
            tags, 
            loading: false, 
            error: null,
            unsubscribeTags: unsubscribe
          });
        },
        (error) => {
          console.error('Error subscribing to tags:', error);
          set({ error: error.message, loading: false });
        }
      );

      set({ unsubscribeTags: unsubscribe, loading: true });
    },

    createTag: async (familyId, userId, tagData) => {
      if (!familyId || !userId) return { success: false, error: 'Missing required parameters' };

      set({ loading: true, error: null });
      try {
        // Check if tag with same name already exists
        const tagsRef = collection(db, `families/${familyId}/tags`);
        const existingTagQuery = query(tagsRef, where('name', '==', tagData.name));
        const existingTagSnapshot = await getDocs(existingTagQuery);
        
        if (!existingTagSnapshot.empty) {
          set({ loading: false, error: 'A tag with this name already exists' });
          return { success: false, error: 'A tag with this name already exists' };
        }

        const newTag = {
          name: tagData.name,
          description: tagData.description || '',
          color: tagData.color || '#6B7280', // Default gray color
          familyId,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: userId,
          usageCount: 0 // Track how many expenses use this tag
        };

        const docRef = await addDoc(tagsRef, newTag);
        
        set({ loading: false, error: null });
        return { success: true, id: docRef.id };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    updateTag: async (familyId, tagId, updates) => {
      if (!familyId || !tagId) return { success: false, error: 'Missing required parameters' };

      set({ loading: true, error: null });
      try {
        // If updating name, check if tag with same name already exists
        if (updates.name) {
          const tagsRef = collection(db, `families/${familyId}/tags`);
          const existingTagQuery = query(
            tagsRef, 
            where('name', '==', updates.name)
          );
          const existingTagSnapshot = await getDocs(existingTagQuery);
          
          // Check if any existing tag has the same name (excluding current tag)
          const conflictingTag = existingTagSnapshot.docs.find(doc => doc.id !== tagId);
          if (conflictingTag) {
            set({ loading: false, error: 'A tag with this name already exists' });
            return { success: false, error: 'A tag with this name already exists' };
          }
        }

        const tagRef = doc(db, `families/${familyId}/tags`, tagId);
        await updateDoc(tagRef, {
          ...updates,
          updatedAt: new Date()
        });

        set({ loading: false, error: null });
        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    deleteTag: async (familyId, tagId) => {
      if (!familyId || !tagId) return { success: false, error: 'Missing required parameters' };

      set({ loading: true, error: null });
      try {
        // Check if tag is being used by any expenses
        const expensesRef = collection(db, `families/${familyId}/expenses`);
        const expensesWithTagQuery = query(expensesRef, where('tags', 'array-contains', tagId));
        const expensesWithTagSnapshot = await getDocs(expensesWithTagQuery);
        
        if (!expensesWithTagSnapshot.empty) {
          set({ loading: false, error: 'Cannot delete tag that is being used by expenses' });
          return { success: false, error: 'Cannot delete tag that is being used by expenses' };
        }

        const tagRef = doc(db, `families/${familyId}/tags`, tagId);
        await deleteDoc(tagRef);

        set({ loading: false, error: null });
        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    getTagById: async (familyId, tagId) => {
      if (!familyId || !tagId) return { success: false, error: 'Missing required parameters' };

      try {
        const tagRef = doc(db, `families/${familyId}/tags`, tagId);
        const tagDoc = await getDoc(tagRef);
        
        if (tagDoc.exists()) {
          return { 
            success: true, 
            tag: { id: tagDoc.id, ...tagDoc.data() } 
          };
        } else {
          return { success: false, error: 'Tag not found' };
        }
      } catch (error) {
        return { success: false, error: error.message };
      }
    },

    getTagsByIds: (tagIds) => {
      const { tags } = get();
      return tags.filter(tag => tagIds.includes(tag.id));
    },

    updateTagUsageCount: async (familyId, tagId, increment = true) => {
      try {
        const tagRef = doc(db, `families/${familyId}/tags`, tagId);
        const tagDoc = await getDoc(tagRef);
        
        if (tagDoc.exists()) {
          const currentCount = tagDoc.data().usageCount || 0;
          const newCount = increment ? currentCount + 1 : Math.max(0, currentCount - 1);
          
          await updateDoc(tagRef, {
            usageCount: newCount,
            updatedAt: new Date()
          });
        }
      } catch (error) {
        console.error('Error updating tag usage count:', error);
      }
    },

    clearError: () => set({ error: null }),

    cleanup: () => {
      const { unsubscribeTags } = get();
      if (unsubscribeTags) {
        unsubscribeTags();
        set({ unsubscribeTags: null });
      }
    }
  }))
);

export default useTagStore;
